import * as CL from '@design-system/component-library';
import { Fragment } from 'react';
import { Highlight } from '../../components/Highlight/Highlight.js';
import { formatTimestampToDDMMYYYY } from './dateUtils';
import { t } from '../i18n';

export type NonVisibleField = {
  name: string;
  getTitle: () => string;
};

const canTextBeHighlighted = (text: string, minLength = 2): boolean => text.length >= minLength;

const getMatchedFields = (fieldsToLookIn: NonVisibleField[], fieldsToLookFor: string[]) => {
  return fieldsToLookIn.filter(field => fieldsToLookFor.includes(field.name));
};

const formatMatchedItemValue = (value: Array<string> | string) => {
  if (Array.isArray(value)) {
    return value.join(', ');
  }
  return value;
};

export const highlightKeyword = (text: string, keyword?: string): string | JSX.Element =>
  keyword && canTextBeHighlighted(keyword) ? <Highlight text={text} highlights={keyword.split(/\s/)} /> : text;

export const getHighlightedFields = <T extends object>(
  data: T,
  fieldsToLookFor: string[],
  keywordToFind: string,
  fieldsToLookIn: NonVisibleField[]
): JSX.Element[] => {
  return getMatchedFields(fieldsToLookIn, fieldsToLookFor)
    .map(matchedField => ({
      // @ts-ignore
      itemValue: data[matchedField.name],
      itemTitle: matchedField.getTitle(),
    }))
    .map(matchedData => (
      <Fragment key={matchedData.itemValue}>
        <span>{matchedData.itemTitle}: </span>
        {highlightKeyword(formatMatchedItemValue(matchedData.itemValue), keywordToFind)}
        <span> </span>
      </Fragment>
    ));
};

export const ScheduledChangeTimestamp = ({
  scheduledChangeTimestamp,
  style,
}: {
  scheduledChangeTimestamp?: number;
  style: string;
}) => {
  if (!scheduledChangeTimestamp) {
    return null;
  }

  return (
    <div className={style}>
      <CL.Tooltip
        triggerElement={<CL.Icon icon="calendar-time" />}
        placement="top"
        i18n_tooltip_contentText={`${t.N4LE('Scheduled change')} ${formatTimestampToDDMMYYYY(scheduledChangeTimestamp)}`}
      />
      <div>{formatTimestampToDDMMYYYY(scheduledChangeTimestamp)}</div>
    </div>
  );
};

export const HighlightSearchResult = <T extends object>(
  esResults: string[],
  object: T,
  columnsToAdd: { columnId: string; translation: string }[],
  searchWord: string
) => {
  return columnsToAdd.map(column => {
    // @ts-ignore
    const col = object[column.columnId];
    if (esResults.includes(column.columnId) && !!col) {
      return (
        <span key={column.columnId}>
          <span>{column.translation}: </span>
          {highlightKeyword(col, searchWord)}
          <br />
        </span>
      );
    }
    return <Fragment key={column.columnId}></Fragment>;
  });
};
